import { useCallback, useState } from 'react';
import { Sheet } from './sheet.js';

/**
 * THE REDESIGN CONFIRM (frame 3f, S2 part 2) — the one destructive-confirm surface, the
 * replacement for every `window.confirm`. It names the object or the count in the title,
 * says in the body what does NOT happen (so an irreversible-looking act is shown to be
 * survivable where it is), and puts the act far from Cancel.
 *
 * `role="alertdialog"` and no grabber: this interrupts for a decision, it is not a sheet to
 * flick away. `elevated` so it stacks above an open picker (the option-sheet's delete
 * confirms open one from inside another sheet).
 *
 * TONE. `danger` (default) is the frame's `--alert-solid` bold pill — for anything that
 * removes a record, an item, or access, even where it is restorable (the frame's own drawn
 * example is "Revoke access for Rune?", which IS restorable and IS red). `plain` is the
 * `--accent-solid` primary — for a recoverable detach or a forward step (remove a re-uploadable
 * photo, close a reopenable retour, send without proof) where red would overstate the stakes.
 */
export function Confirm({
  title,
  body,
  confirmLabel,
  tone = 'danger',
  onConfirm,
  onClose,
}: {
  /** The question, naming the object/count: "Revoke access for Rune?", "Delete Box 12?". */
  title: string;
  /** What does NOT happen / how to undo: "Nothing is deleted, and you can restore access." */
  body: string;
  /** The act's own words: "Revoke access", "Delete box". */
  confirmLabel: string;
  tone?: 'danger' | 'plain';
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Sheet label={title} onClose={onClose} className="confirm-sheet" grabber={false} elevated role="alertdialog">
      <p className="confirm-title">{title}</p>
      <p className="confirm-body">{body}</p>
      <p className="confirm-actions">
        <button type="button" className="confirm-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </p>
    </Sheet>
  );
}

export interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  tone?: 'danger' | 'plain';
}

/**
 * `const [confirmEl, ask] = useConfirm()` — the drop-in for the browser's blocking confirm. Render
 * `{confirmEl}` once anywhere in the component, and replace the native call with
 * `await ask({ title, body, confirmLabel })`. `ask` resolves `true` on the act, `false` on
 * Cancel / scrim / Escape / back — exactly the boolean `window.confirm` returned, so a call
 * site keeps its `if (!(await ask(...))) return;` shape and does the write itself, on its own
 * timeout/classification path. One hook per component covers all of that component's confirms
 * (only one is ever open at a time); the sheet closes on the choice, so the write then runs
 * with the screen's own busy indicator, as it did behind the blocking prompt.
 */
export function useConfirm(): [JSX.Element | null, (request: ConfirmRequest) => Promise<boolean>] {
  const [pending, setPending] = useState<{ request: ConfirmRequest; resolve: (ok: boolean) => void } | null>(null);

  const ask = useCallback(
    (request: ConfirmRequest) => new Promise<boolean>((resolve) => setPending({ request, resolve })),
    [],
  );

  const settle = (ok: boolean): void => {
    setPending((current) => {
      current?.resolve(ok);
      return null;
    });
  };

  const element =
    pending === null ? null : (
      <Confirm
        title={pending.request.title}
        body={pending.request.body}
        confirmLabel={pending.request.confirmLabel}
        tone={pending.request.tone}
        onConfirm={() => {
          settle(true);
        }}
        onClose={() => {
          settle(false);
        }}
      />
    );

  return [element, ask];
}
